import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useUploadEnqueue } from "./app/transferQueue";
import { encodeKey } from "./FileGrid";

const TEXT_EXTENSIONS = new Set([
  "txt", "md", "sh", "bash", "zsh", "py", "js", "ts", "jsx", "tsx",
  "css", "html", "htm", "xml", "json", "yml", "yaml", "toml",
  "conf", "cfg", "ini", "log", "env", "csv", "sql", "lua",
  "rb", "go", "rs", "c", "h", "cpp", "hpp", "java", "kt",
  "swift", "r", "pl", "php", "vue", "svelte", "astro",
  "dockerfile", "makefile", "gitignore", "editorconfig",
]);

export function isTextFile(key: string, contentType?: string): boolean {
  if (contentType?.startsWith("text/")) return true;
  if (contentType === "application/json") return true;
  if (contentType === "application/javascript") return true;
  if (contentType === "application/xml") return true;
  if (contentType === "application/x-yaml") return true;
  if (contentType === "application/toml") return true;
  const ext = key.split(".").pop()?.toLowerCase() ?? "";
  const basename = key.split("/").pop()?.toLowerCase() ?? "";
  return TEXT_EXTENSIONS.has(ext) || TEXT_EXTENSIONS.has(basename);
}

interface TextPadDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  cwd: string;
  onUpload: () => void;
  editFileKey?: string | null;
  onEditDone?: () => void;
}

const TextPadDrawer: React.FC<TextPadDrawerProps> = ({
  open,
  setOpen,
  cwd,
  onUpload,
  editFileKey,
  onEditDone,
}) => {
  const [noteText, setNoteText] = useState("");
  const [noteName, setNoteName] = useState("note.txt");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const uploadEnqueue = useUploadEnqueue();

  const isEditMode = !!editFileKey;

  useEffect(() => {
    if (!open || !editFileKey) return;
    setLoading(true);
    const filename = editFileKey.split("/").pop() ?? editFileKey;
    setNoteName(filename);
    fetch(`/webdav/${encodeKey(editFileKey)}?_=${Date.now()}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch file");
        return res.text();
      })
      .then((text) => setNoteText(text))
      .catch(() => setNoteText(""))
      .finally(() => setLoading(false));
  }, [open, editFileKey]);

  const handleClose = () => {
    setOpen(false);
    setNoteText("");
    setNoteName("note.txt");
    onEditDone?.();
  };

  const handleSave = async () => {
    const finalText = noteText.length > 0 && !noteText.endsWith("\n") ? noteText + "\n" : noteText;
    if (isEditMode && editFileKey) {
      setSaving(true);
      try {
        const blob = new Blob([finalText], { type: "text/plain" });
        const res = await fetch(`/webdav/${encodeKey(editFileKey)}`, {
          method: "PUT",
          body: blob,
          credentials: "include",
        });
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          alert(`Save failed (${res.status}): ${body || res.statusText}`);
          setSaving(false);
          return;
        }
      } catch (e: any) {
        alert(`Save error: ${e.message}`);
        setSaving(false);
        return;
      }
      setSaving(false);
      onUpload();
      handleClose();
    } else {
      const fileBlob = new Blob([finalText], { type: "text/plain" });
      const file = new File([fileBlob], noteName, { type: "text/plain" });
      uploadEnqueue({ file, basedir: cwd });
      onUpload();
      handleClose();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: { xs: "100vw", sm: 500 }, padding: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">
            {isEditMode ? noteName : "TextPad"}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {!isEditMode && (
          <TextField
            label="File Name"
            value={noteName}
            onChange={(e) => setNoteName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TextField
            label={isEditMode ? undefined : "Write your note..."}
            multiline
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            sx={{
              flexGrow: 1,
              "& .MuiInputBase-root": { fontFamily: "monospace", fontSize: "0.85rem", alignItems: "flex-start" },
              "& .MuiInputBase-input": { overflow: "auto !important" },
            }}
            InputProps={{ sx: { height: "100%" } }}
          />
        )}

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSave}
          disabled={saving || loading || (!isEditMode && !noteText.trim())}
        >
          {saving ? "Saving..." : isEditMode ? "Save" : "Save & Upload"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default TextPadDrawer;
